// src/pages/CoursePage.js
import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css'; // Import Plyr styles

// ================= Styled Components (same as before) =================
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 80px 0 60px;
  background: linear-gradient(135deg, #f5f5f7 0%, #ffffff 50%, #f5f5f7 100%);
  border-radius: 24px;
  margin-bottom: 80px;
  position: relative;

  @media (max-width: 768px) {
    padding: 50px 0 40px;
    margin-bottom: 60px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 0 30px;
    margin-bottom: 40px;
    border-radius: 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  font-weight: 700;
  background: linear-gradient(45deg, #1d1d1f, #424245, #1d1d1f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 24px;
  letter-spacing: -1px;
  line-height: 1.1;
  padding: 0 20px;

  @media (max-width: 1024px) {
    font-size: 56px;
  }

  @media (max-width: 768px) {
    font-size: 42px;
    margin-bottom: 20px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 16px;
    padding: 0 12px;
    letter-spacing: -0.5px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: #86868b;
  font-weight: 400;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.4;
  padding: 0 30px;

  @media (max-width: 1024px) {
    font-size: 24px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 32px;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 24px;
    padding: 0 16px;
  }
`;

const SectionContainer = styled.div`
  margin-bottom: 60px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    margin-bottom: 32px;
  }
`;

const SectionCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 28px 24px;
    margin-bottom: 30px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
    margin-bottom: 24px;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: #1d1d1f;
  margin-bottom: 16px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 10px;
  }
`;

const SectionDescription = styled.p`
  font-size: 18px;
  color: #515154;
  line-height: 1.5;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

// New styled wrapper for Plyr container
const PlyrWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  aspect-ratio: 16 / 9;

  .plyr {
    border-radius: 16px;
  }

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SectionDivider = styled.hr`
  margin: 60px 0 20px;
  border: none;
  height: 2px;
  background: linear-gradient(90deg, transparent, #0071e3, transparent);
  opacity: 0.3;

  @media (max-width: 768px) {
    margin: 40px 0 20px;
  }
`;

const GlobalSectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(45deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 60px;
  letter-spacing: -0.5px;

  @media (max-width: 1024px) {
    font-size: 42px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 32px;
  }
`;

const ShieldNote = styled.div`
  text-align: center;
  margin-top: 60px;
  padding: 20px;
  background: rgba(0, 113, 227, 0.05);
  border-radius: 16px;
  color: #6e6e73;
  font-size: 14px;
  border: 1px solid rgba(0, 113, 227, 0.1);

  @media (max-width: 480px) {
    margin-top: 40px;
    padding: 16px;
    font-size: 12px;
  }
`;

// ================= Plyr Video Component =================
// This component uses Plyr to embed YouTube without any visible YouTube branding/links.
// The underlying iframe is still created by Plyr, but the custom UI replaces all controls.
const PlyrVideo = ({ videoId, title }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    // Initialize Plyr only after the container is mounted
    if (!containerRef.current) return;

    // Plyr options – these control the YouTube player parameters and the custom UI
    const options = {
      // YouTube-specific parameters (passed to the underlying iframe)
      youtube: {
        noCookie: true,           // use youtube-nocookie.com
        rel: 0,                   // no related videos at end
        modestbranding: 1,        // reduce YouTube logo
        iv_load_policy: 3,        // disable annotations
        controls: 1,              // show controls (Plyr will still overlay its own)
        fs: 1,                    // allow fullscreen
        // showinfo is deprecated, but modestbranding and rel=0 already help
      },
      // Customize the Plyr control bar – omit any button that could link to YouTube
      controls: [
        'play-large',    // large play button
        'play',          // play/pause
        'progress',      // progress bar
        'current-time',  // current time
        'mute',          // mute
        'volume',        // volume slider
        'fullscreen',    // fullscreen toggle
        // 'settings',    // optional (no YouTube link)
        // 'pip',         // picture-in-picture
      ],
      // Disable the default context menu on the Plyr container (additional protection)
      tooltips: { controls: false },
      keyboard: { focused: true, global: false },
    };

    // Create the Plyr instance
    playerRef.current = new Plyr(containerRef.current, options);

    // Cleanup on unmount
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [videoId]); // re-run if videoId changes (though it shouldn't in this component)

  // Prevent right-click on the entire video area (blocks any "Copy video URL" attempt)
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  return (
    <VideoContainer onContextMenu={handleContextMenu}>
      <PlyrWrapper>
        {/* 
          Plyr requires a container with data-plyr-provider and data-plyr-embed-id.
          The actual YouTube URL is never written as text; Plyr constructs it internally.
        */}
        <div
          ref={containerRef}
          data-plyr-provider="youtube"
          data-plyr-embed-id={videoId}
          data-plyr-title={title}
        />
      </PlyrWrapper>
    </VideoContainer>
  );
};

// ================= Course Sections Data (unchanged) =================
const COURSE_SECTIONS = [
  {
    id: 1,
    title: "System Fundamentals: Power 101",
    description: "",
    videoId: "VfB-o_ZuuNE",
  },
  {
    id: 2,
    title: "System Fundamentals: DC Transmission Lines",
    description: "",
    videoId: "PUkVEHUzH9M",
  },
  {
    id: 3,
    title: "Overhead: Construction Of Overhead Lines",
    description: "",
    videoId: "2qPARDiFZf4",
  },
  {
    id: 4,
    title: "Overhead: Line Conductors",
    description: "",
    videoId: "W_8o_Ajcq-8",
  },
  {
    id: 5,
    title: "Overhead: Insulators",
    description: "",
    videoId: "KkRCxQM2KFU",
  },
  {
    id: 6,
    title: "Overhead: Pole Line Hardware",
    description: "",
    videoId: "gc-auw8p-jY",
  },
  {
    id: 7,
    title: "Overhead: On Pole Equipment",
    description: "",
    videoId: "1d7PQWwDpU4",
  },
  {
    id: 8,
    title: "Overhead: Where Guys Should Be Used",
    description: "",
    videoId: "-SdKPSzq0yA",
  },
  {
    id: 9,
    title: "Underground System",
    description: "",
    videoId: "w3WP-gRYaGg",
  },
  {
    id: 10,
    title: "Underground Cables",
    description: "",
    videoId: "_a80Kl-A18M",
  },
  {
    id: 11,
    title: "Protection",
    description: "",
    videoId: "8DemElTgg0A",
  },
    {
    id: 12,
    title: "Grounding",
    description: "",
    videoId: "fA9ep2U9hlU",
  },
   {
    id: 13,
    title: "Insulation Coordination",
    description: "",
    videoId: "DupmQy6NAXE",
  },
  {
    id: 14,
    title: "Disturbances",
    description: "",
    videoId: "9SqAXtiAOfM",
  },
  {
    id: 15,
    title: "Secondary Network Distribution Systems",
    description: "",
    videoId: "RrO5Z1Bdkyw",
  },
  {
    id: 16,
    title: "Design Basics: Line Design Process",
    description: "",
    videoId: "cZCX-a8matA",
  },
  {
    id: 17,
    title: "Design Basics: Overhead Line Calculation",
    description: "",
    videoId: "Xh9V2YdUDLY",
  },
  {
    id: 18,
    title: "Design Basics: Calculating Cable Pull",
    description: "",
    videoId: "zcKkXe1Xfd4",
  },
];

// ================= Main Component =================
const LFundCourse = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Power Transmission and Distribution Poles and Lines Fundamentals</HeroTitle>
        <HeroSubtitle>
          A comprehensive video course where you can learn industry-specific knowledge pertaining to the T&D infrastructure as well as how it is designed.
        </HeroSubtitle>
      </HeroSection>

      <GlobalSectionTitle>📺 Course Modules (Video Lessons)</GlobalSectionTitle>

      {COURSE_SECTIONS.map((section, index) => (
        <SectionContainer key={section.id}>
          <SectionCard>
            <SectionTitle>{section.title}</SectionTitle>
            <SectionDescription>{section.description}</SectionDescription>
            {/* Replace the old ProtectedVideo with PlyrVideo */}
            <PlyrVideo videoId={section.videoId} title={section.title} />
          </SectionCard>
          {index < COURSE_SECTIONS.length - 1 && <SectionDivider />}
        </SectionContainer>
      ))}

      <ShieldNote>
        End of Course
        </ShieldNote>
    </PageContainer>
  );
};

export default LFundCourse;